// src/pages/AdminOrdini.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import OrderPeriodPicker from "../../components/OrderPeriodPicker";
import toast from "react-hot-toast";
import { listAdminOrders, updateAdminOrderStatus } from "../../api/ordersApi";
import OrderDetailModal from "../../components/orders/OrderDetailModal";
import "../css/AdminOrdiniAdmin.css";

/* ---------- utils ---------- */
function formatCurrency(v) {
  const n = Number(v);
  const isCents = Number.isInteger(n) && n >= 50;
  const eur = isCents ? n / 100 : n;
  return eur.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("it-IT");
}

function formatDateLabel(dateIso) {
  if (!dateIso) return "—";
  return formatDate(dateIso);
}

function formatDateWithSlot(dateIso, slot) {
  const d = formatDateLabel(dateIso);
  if (!slot || slot === "—") return d;
  return `${d} · ${slot}`;
}

// helper: leggi ruolo dal JWT in localStorage (solo per info UI)
function getRoleFromToken() {
  try {
    const t = localStorage.getItem("token");
    if (!t) return null;
    const base64 = t.split(".")[1];
    const json = JSON.parse(atob(base64.replace(/-/g, "+").replace(/_/g, "/")));
    return String(json?.role || json?.user?.role || "").toLowerCase() || null;
  } catch {
    return null;
  }
}

/* ---------- costanti ---------- */
const PAGE_SIZE = 50;

const STATUS_OPTIONS = [
  { value: "all", label: "Tutti" },
  { value: "pag", label: "Pagato" },
  { value: "ritir", label: "Ritirato" },
  { value: "incons", label: "Da Consegnare" },
  { value: "consegn", label: "Consegnato" },
  { value: "not_consegn", label: "<> Consegnato" },
];

// stati DB reali per la select in-line
const DB_STATUS_OPTIONS = [
  { value: "pagata", label: "Pagato" },
  { value: "ritirata", label: "Ritirato" },
  { value: "completata", label: "Completato" },
  { value: "in_consegna", label: "Da Consegnare" },
  { value: "consegnata", label: "Consegnato" },
];

const FILTER_TO_DB_STATUS = {
  pag: ["pagata"],
  ritir: ["ritirata"],
  incons: ["completata", "in_consegna"],
  consegn: ["consegnata"],
};

function statusMatches(rawStatus, filterKey) {
  if (!filterKey || filterKey === "all") return true;
  if (filterKey === "not_consegn") return rawStatus !== "consegnata";
  const targets = FILTER_TO_DB_STATUS[filterKey];
  if (!targets) return true;
  return targets.includes(rawStatus);
}

function humanize(raw) {
  switch (raw) {
    case "in_attesa": return "In attesa";
    case "accettata": return "Accettata";
    case "pagata": return "Pagata";
    case "ritirata": return "Ritirata";
    case "completata": return "Completata";
    case "in_consegna": return "In consegna";
    case "consegnata": return "Consegnato";
    case "annullata": return "Annullata";
    case "rifiutata": return "Rifiutata";
    default: return "In lavorazione";
  }
}

export default function AdminOrdini() {
  const [searchParams, setSearchParams] = useSearchParams();
  const role = getRoleFromToken();
  const isAdminClient = role === "admin";

  // UI state
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [cliente, setCliente] = useState(searchParams.get("cliente") || "");
  const [provincia, setProvincia] = useState(searchParams.get("provincia") || "");
  const [citta, setCitta] = useState(searchParams.get("citta") || "");
  const [fornitore, setFornitore] = useState(searchParams.get("fornitore") || "");

  // Filtri applicati (BE)
  const [filters, setFilters] = useState({
    status: searchParams.get("status") || "all",
    from: searchParams.get("from") || "",
    to: searchParams.get("to") || "",
    cliente: searchParams.get("cliente") || "",
    provincia: searchParams.get("provincia") || "",
    citta: searchParams.get("citta") || "",
    fornitore: searchParams.get("fornitore") || "",
  });

  const [page, setPage] = useState(Number(searchParams.get("page") || 1));
  const [orders, setOrders] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  const [updating, setUpdating] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  // sorting locale (pagina corrente)
  // (per allinearti a contabilità: data desc di default)
  const [sort, setSort] = useState({ field: "date", dir: "desc" });

  // MODALE dettaglio
  const [detailId, setDetailId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  /* ---------- sync querystring ---------- */
  useEffect(() => {
    const qp = new URLSearchParams();
    qp.set("page", String(page));
    if (filters.status && filters.status !== "all") qp.set("status", filters.status);
    if (filters.from) qp.set("from", filters.from);
    if (filters.to) qp.set("to", filters.to);
    if (filters.cliente) qp.set("cliente", filters.cliente);
    if (filters.provincia) qp.set("provincia", filters.provincia);
    if (filters.citta) qp.set("citta", filters.citta);
    if (filters.fornitore) qp.set("fornitore", filters.fornitore);
    setSearchParams(qp, { replace: true });
  }, [page, filters, setSearchParams]);

  /* Text filters retain the existing debounce; period/state apply immediately. */
  useEffect(() => {
    const handle = setTimeout(() => {
      if (cliente === filters.cliente && provincia === filters.provincia && citta === filters.citta && fornitore === filters.fornitore) return;
      setPage(1);
      setFilters(prev => ({ ...prev, cliente, provincia, citta, fornitore }));
    }, 400);
    return () => clearTimeout(handle);
  }, [cliente, provincia, citta, fornitore, filters.cliente, filters.provincia, filters.citta, filters.fornitore]);

  /* ---------- fetch lista ordini ---------- */
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setOrders([]);

    listAdminOrders({ page, pageSize: PAGE_SIZE, filters })
      .then((res) => {
        if (!alive) return;
        setOrders(Array.isArray(res.data) ? res.data : []);
        setMeta({
          page: Number(res.page || page),
          totalPages: Number(res.totalPages || 1),
        });
      })
      .catch((err) => {
        if (!alive) return;
        console.error("[AdminOrdini] listAdminOrders error:", err?.message || err);
        toast.error("Impossibile caricare gli ordini (admin)");
      })
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [page, filters, refreshKey]);

  const rows = useMemo(() => orders || [], [orders]);

  /* ---------- ordinamento locale ---------- */
  function toggleSort(field) {
    setSort((prev) => {
      if (prev.field === field) return { field, dir: prev.dir === "asc" ? "desc" : "asc" };
      return { field, dir: "asc" };
    });
  }

  function sortArrow(field) {
    if (sort.field !== field) return "↕";
    return sort.dir === "asc" ? "↑" : "↓";
  }

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const { field, dir } = sort;
    const mult = dir === "asc" ? 1 : -1;

    arr.sort((a, b) => {
      const getDate = (o) => new Date(o.created_at || o.data || o.createdAt || 0).getTime() || 0;
      const getStr = (v) => String(v || "").toLowerCase();
      const getTotal = (o) => Number(o.total_cents ?? o.totale_cents ?? o.total_eur ?? o.total ?? 0);

      let va, vb;

      switch (field) {
        case "cliente":
          va = getStr(a.cliente); vb = getStr(b.cliente); return va.localeCompare(vb) * mult;
        case "fornitore":
          va = getStr(a.fornitore); vb = getStr(b.fornitore); return va.localeCompare(vb) * mult;
        case "citta":
          va = getStr(a.citta); vb = getStr(b.citta); return va.localeCompare(vb) * mult;
        case "provincia":
          va = getStr(a.provincia); vb = getStr(b.provincia); return va.localeCompare(vb) * mult;
        case "total":
          va = getTotal(a); vb = getTotal(b); return (va - vb) * mult;
        case "status":
          va = getStr(a.status || a.raw_status); vb = getStr(b.status || b.raw_status); return va.localeCompare(vb) * mult;
        case "order":
          va = Number(a.id || 0); vb = Number(b.id || 0); return (va - vb) * mult;
        case "invoice":
          va = getStr(a.invoice_number); vb = getStr(b.invoice_number); return va.localeCompare(vb) * mult;
        case "date":
        default:
          va = getDate(a); vb = getDate(b); return (va - vb) * mult;
      }
    });

    return arr;
  }, [rows, sort]);

  /* ---------- azioni filtri manuali ---------- */
  function applyPeriod(range) {
    if (range.from && range.to && range.from > range.to) return;
    if (filters.from === range.from && filters.to === range.to) return;
    setPage(1);
    setFilters(prev => ({ ...prev, ...range }));
  }

  function resetFilters() {
    setStatus("all");
    setCliente("");
    setProvincia("");
    setCitta("");
    setFornitore("");
    setPage(1);
    setFilters({
      status: "all",
      from: "",
      to: "",
      cliente: "",
      provincia: "",
      citta: "",
      fornitore: "",
    });
  }

  /* ---------- inline status change ---------- */
  async function changeRowStatus(order, next) {
    const prev = order.raw_status;

    setOrders((list) =>
      list.map((o) => (o.id === order.id ? { ...o, raw_status: next, status: humanize(next) } : o))
    );
    setUpdating((u) => ({ ...u, [order.id]: true }));

    try {
      await updateAdminOrderStatus(order.id, next);

      if (!statusMatches(next, filters.status)) {
        setOrders((prevList) => {
          const wasSingle = prevList.length === 1;
          const newList = prevList.filter((o) => o.id !== order.id);
          if (wasSingle && page > 1) {
            setPage(page - 1);
          } else {
            setRefreshKey((k) => k + 1);
          }
          return newList;
        });
        toast.success("Stato aggiornato · filtro applicato");
      } else {
        toast.success("Stato aggiornato");
      }
    } catch (err) {
      setOrders((list) =>
        list.map((o) => (o.id === order.id ? { ...o, raw_status: prev, status: humanize(prev) } : o))
      );
      toast.error(err?.message || "Errore aggiornamento stato");
    } finally {
      setUpdating((u) => {
        const { [order.id]: _drop, ...rest } = u;
        return rest;
      });
    }
  }

  /* ---------- modale dettaglio ---------- */
  function openOrderDetail(id) {
    if (!id) return;
    setDetailId(id);
    setDetailOpen(true);
  }

  function closeOrderDetail() {
    setDetailOpen(false);
    setDetailId(null);
  }

  return (
    <div className="admord-page">
      <div className="admord-inner">
        <header className="admord-header">
          <div>
            <h1>Ordini (admin)</h1>
            {!isAdminClient && (
              <p className="admord-sub">
                (nota: nel token non risulti admin, decide il backend se farti entrare)
              </p>
            )}
          </div>
        </header>

        {/* FILTRI (stile contabile) */}
        <section className="admord-filters admord-filters--range">
          <div className="admord-filter-group">
            <label className="admord-label">Cliente</label>
            <input
              className="admord-input"
              type="search"
              value={cliente}
              onChange={(e) => setCliente(e.target.value)}
              placeholder="Nome o email"
            />
          </div>

          <div className="admord-filter-group">
            <label className="admord-label">Fornitore</label>
            <input
              className="admord-input"
              type="search"
              value={fornitore}
              onChange={(e) => setFornitore(e.target.value)}
              placeholder="Nome fornitore"
            />
          </div>

          <div className="admord-filter-group">
            <label className="admord-label">Provincia</label>
            <input
              className="admord-input"
              type="text"
              value={provincia}
              onChange={(e) => setProvincia(e.target.value)}
              placeholder="Es. MI"
            />
          </div>

          <div className="admord-filter-group">
            <label className="admord-label">Città</label>
            <input
              className="admord-input"
              type="text"
              value={citta}
              onChange={(e) => setCitta(e.target.value)}
              placeholder="Es. Milano"
            />
          </div>

          <div className="admord-filter-group">
            <label className="admord-label">Stato ordine</label>
            <select className="admord-input admord-select" value={status} onChange={(e) => { const next = e.target.value; setStatus(next); if (next !== filters.status) { setPage(1); setFilters(prev => ({ ...prev, status: next })); } }}
              aria-label="Stato ordine">
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="admord-filter-group">
            <span className="admord-label">Periodo</span>
            <OrderPeriodPicker from={filters.from} to={filters.to} onApply={applyPeriod} />
          </div>

          <div className="admord-filter-actions">
            <button className="admord-btn admord-btn-ghost" onClick={resetFilters}>
              Pulisci
            </button>
          </div>
        </section>

        {/* TABELLA (stile contabile) */}
        <section className="admord-table-section">
          <div className="admord-table-wrap">
            <table className="admord-table" aria-label="Elenco ordini admin">
              <thead>
                <tr>
                  <th className="admord-col-date">
                    <button type="button" className="admord-th-btn" onClick={() => toggleSort("date")}>
                      <span>Data ordine</span>
                      <span className="admord-th-arrow">{sortArrow("date")}</span>
                    </button>
                  </th>

                  <th className="admord-col-id">
                    <button type="button" className="admord-th-btn admord-th-btn--right" onClick={() => toggleSort("order")}>
                      <span>Ord.</span>
                      <span className="admord-th-arrow">{sortArrow("order")}</span>
                    </button>
                  </th>

                  <th className="admord-col-cliente">
                    <button type="button" className="admord-th-btn" onClick={() => toggleSort("cliente")}>
                      <span>Cliente</span>
                      <span className="admord-th-arrow">{sortArrow("cliente")}</span>
                    </button>
                  </th>

                  <th className="admord-col-fornitore">
                    <button type="button" className="admord-th-btn" onClick={() => toggleSort("fornitore")}>
                      <span>Fornitore</span>
                      <span className="admord-th-arrow">{sortArrow("fornitore")}</span>
                    </button>
                  </th>

                  <th className="admord-col-citta">
                    <button type="button" className="admord-th-btn" onClick={() => toggleSort("citta")}>
                      <span>Città</span>
                      <span className="admord-th-arrow">{sortArrow("citta")}</span>
                    </button>
                  </th>

                  <th className="admord-col-provincia">
                    <button type="button" className="admord-th-btn" onClick={() => toggleSort("provincia")}>
                      <span>Provincia</span>
                      <span className="admord-th-arrow">{sortArrow("provincia")}</span>
                    </button>
                  </th>

                  <th className="admord-col-importo">
                    <button type="button" className="admord-th-btn admord-th-btn--right" onClick={() => toggleSort("total")}>
                      <span>Importo</span>
                      <span className="admord-th-arrow">{sortArrow("total")}</span>
                    </button>
                  </th>

                  <th className="admord-col-ft">
                    <button type="button" className="admord-th-btn admord-th-btn--center" onClick={() => toggleSort("invoice")}>
                      <span>FT</span>
                      <span className="admord-th-arrow">{sortArrow("invoice")}</span>
                    </button>
                  </th>

                  <th className="admord-col-status">
                    <button type="button" className="admord-th-btn" onClick={() => toggleSort("status")}>
                      <span>Stato ordine</span>
                      <span className="admord-th-arrow">{sortArrow("status")}</span>
                    </button>
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading &&
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={`skel-${i}`} className="admord-row-skel">
                      <td><span className="admord-skel-bar" /></td>
                      <td><span className="admord-skel-bar admord-skel-bar--short" /></td>
                      <td><span className="admord-skel-bar admord-skel-bar--wide" /></td>
                      <td><span className="admord-skel-bar admord-skel-bar--wide" /></td>
                      <td><span className="admord-skel-bar" /></td>
                      <td><span className="admord-skel-bar admord-skel-bar--short" /></td>
                      <td><span className="admord-skel-bar" /></td>
                      <td><span className="admord-skel-bar admord-skel-bar--short" /></td>
                      <td><span className="admord-skel-bar admord-skel-bar--wide" /></td>
                    </tr>
                  ))}

                {!loading && sortedRows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="admord-empty">Nessun ordine trovato.</td>
                  </tr>
                )}

                {!loading && sortedRows.length > 0 && sortedRows.map((o) => (
                  <tr key={o.id}>
                    <td className="admord-col-date">
                      <div className="admord-datecell">
                        <span className="admord-date-main">
                          {formatDate(o.created_at || o.data || o.createdAt)}
                        </span>

                        {/* tooltip info ritiro/consegna (uguale comportamento, nuovo stile) */}
                        <span className="admord-tt">
                          <button
                            type="button"
                            className="admord-tt-btn"
                            aria-label="Mostra info ritiro e consegna"
                            title="Ritiro / Consegna"
                          >
                            i
                          </button>

                          <span className="admord-tt-bubble" role="tooltip">
                            <div className="admord-tt-title">Ritiro / Consegna</div>
                            <div className="admord-tt-line">
                              <strong>Ritiro:</strong> {formatDateWithSlot(o.data_ritiro, o.ritiro_fascia)}
                            </div>
                            <div className="admord-tt-line">
                              <strong>Consegna:</strong> {formatDateWithSlot(o.data_consegna, o.consegna_fascia)}
                            </div>
                          </span>
                        </span>
                      </div>
                    </td>

                    <td className="admord-col-id">
                      <button
                        type="button"
                        className="admord-link-id"
                        onClick={() => openOrderDetail(o.id)}
                        title={`Apri ordine #${o.id}`}
                      >
                        <svg className="admord-eye-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        {o.id}
                      </button>
                    </td>

                    <td className="admord-col-cliente">{o.cliente || "—"}</td>
                    <td className="admord-col-fornitore">{o.fornitore || "—"}</td>
                    <td className="admord-col-citta">{o.citta || "—"}</td>
                    <td className="admord-col-provincia">{o.provincia || "—"}</td>

                    <td className="admord-col-importo">
                      {formatCurrency(o.total_cents ?? o.totale_cents ?? o.total_eur ?? o.total)}
                    </td>

                    <td className="admord-col-ft">
                      {o.invoice_number ? String(o.invoice_number) : ""}
                    </td>

                    <td className="admord-col-status">
                      <select
                        className="admord-inrow-select"
                        data-status={o.raw_status || ""}
                        value={o.raw_status || ""}
                        onChange={(e) => changeRowStatus(o, e.target.value)}
                        disabled={!!updating[o.id] || loading}
                      >
                        {DB_STATUS_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginazione (stile contabile) */}
          {meta.totalPages > 1 && (
            <div className="admord-pagination">
              <button
                type="button"
                className="admord-btn admord-btn-ghost"
                onClick={() => setPage(Math.max(1, (meta.page || 1) - 1))}
                disabled={loading || (meta.page || 1) <= 1}
              >
                ← Prec.
              </button>
              <span className="admord-page-info">
                Pagina {meta.page} / {meta.totalPages}
              </span>
              <button
                type="button"
                className="admord-btn admord-btn-ghost"
                onClick={() => setPage(Math.min(meta.totalPages, (meta.page || 1) + 1))}
                disabled={loading || (meta.page || 1) >= meta.totalPages}
              >
                Succ. →
              </button>
            </div>
          )}
        </section>
      </div>
      <OrderDetailModal
        open={detailOpen}
        orderId={detailId}
        onClose={closeOrderDetail}
      />
    </div>
  );
}
